import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/ContactForm.css';

interface DateFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  error?: string;
  required?: boolean;
  min?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// Native date inputs keep a typed two-digit year as-is (28.04.25 becomes 0025-04-28),
// so expand any year below 100 to the nearest sensible century.
const normalizeTwoDigitYear = (value: string): string => {
  const match = /^(\d{4})(-\d{2}-\d{2})$/.exec(value);
  if (!match) {
    return value;
  }

  const year = parseInt(match[1], 10);
  if (year < 1 || year >= 100) {
    return value;
  }

  const currentYear = new Date().getFullYear();
  const expanded = 2000 + year <= currentYear ? 2000 + year : 1900 + year;
  return `${expanded}${match[2]}`;
};

const DateField: React.FC<DateFieldProps> = ({
  id,
  name,
  label,
  value,
  error,
  required = false,
  min,
  onChange
}) => {
  const { t } = useTranslation();
  const dateInputRef = useRef<HTMLInputElement>(null);
  const requiredLabel = t('accessibility.required');

  const handleDateClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (dateInputRef.current) {
      // Try showPicker first (modern browsers) - this works because click is a direct user gesture
      if (dateInputRef.current.showPicker) {
        try {
          dateInputRef.current.showPicker();
        } catch (err) {
          // If showPicker fails, the native click behavior will still work
          console.debug('showPicker not available or failed:', err);
        }
      }
    }
  };

  // Normalized on blur rather than on change, so rewriting the value cannot
  // interfere with the year segment while the user is still typing it.
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const normalized = normalizeTwoDigitYear(e.target.value);
    if (normalized !== e.target.value) {
      e.target.value = normalized;
      onChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <div className="form-group">
      <label htmlFor={id}>
        {label} {required && <span aria-label={requiredLabel}>*</span>}
      </label>
      <input
        ref={dateInputRef}
        type="date"
        id={id}
        name={name}
        value={value}
        min={min}
        onChange={onChange}
        onBlur={handleBlur}
        onClick={handleDateClick}
        required={required}
        aria-describedby={error ? `${id}-error` : undefined}
        aria-invalid={!!error}
      />
      {error && (
        <div id={`${id}-error`} className="form-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default DateField;

